import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Shell } from './components/Shell';
import { useSession } from './lib/session';
import { auth, setUnauthorizedHandler } from './data/live';
import { Stations } from './screens/Stations';
import { ChargePoints } from './screens/ChargePoints';
import { Connectors } from './screens/Connectors';
import { TestBench } from './screens/TestBench';
import { Agents } from './screens/Agents';
import { Activity } from './screens/Activity';
import { ApiUsage } from './screens/ApiUsage';
import { Legal } from './screens/Legal';
import { Dashboard, Ocpp, Releases } from './screens/Misc';
import { Transactions } from './screens/Money';
import { ConnectorTypes } from './screens/ConnectorTypes';
import { ChargerModels } from './screens/ChargerModels';
import { Documents } from './screens/Documents';
import { VehicleDocuments } from './screens/VehicleDocuments';
import { TopUps } from './screens/TopUps';
import { AgentFloat } from './screens/AgentFloat';
import { Usage } from './screens/Usage';
import { Disputes, Drivers, Maps, Tariffs, Users } from './screens/System';

/** What the server called wrong, said in words an operator can act on. */
const MESSAGES: Record<string, string> = {
  invalid_credentials: 'That email and password do not match an account.',
  account_locked: 'Too many failed attempts. This account is locked for 15 minutes.',
  account_suspended: 'This account has been suspended.',
  email_and_password_required: 'Enter both an email and a password.',
  current_password_wrong: 'That is not your current password.',
  password_too_short: 'Use at least 12 characters. Length beats punctuation.',
  password_unchanged: 'The new password must be different from the current one.',
  no_session: 'Your session has ended. Sign in again.',
  session_replayed: 'That session was ended for safety. Sign in again.',

  // Codes.
  email_and_code_required: 'Enter the code from your email.',
  code_invalid: 'That code is not right.',
  code_expired: 'That code has expired. Ask for a new one.',
  no_code: 'No code is waiting for that address. Ask for a new one.',
  too_many_attempts: 'Too many wrong codes. Start again.',
  cooldown: 'A code was just sent. Wait a moment before asking for another.',
  too_many_requests: 'Too many codes asked for. Try again later.',
  /* This one is worth being blunt about: the password was accepted and the
     account is fine, so "invalid credentials" would send the operator hunting
     in exactly the wrong place. */
  code_not_sent: 'Your password was right, but the code could not be emailed. '
    + 'Tell your administrator to check the email settings.',
  mail_disabled: 'Email sending is switched off in the console settings.',
  mail_host_missing: 'No SMTP host is configured.',
  mail_from_missing: 'No from-address is configured.',
  send_failed: 'The mail server refused the message. See the delivery log.',
};
const say = (e: unknown) => {
  const k = e instanceof Error ? e.message : '';
  return MESSAGES[k] ?? 'Something went wrong. Try again.';
};

function Card({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="login">
      <div className="card login-card">
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, margin: '0 auto 10px',
            background: 'var(--primary)', color: 'var(--on-primary)',
            display: 'grid', placeItems: 'center', fontSize: 22,
          }}>⚡</div>
          <div style={{ fontSize: 18, fontWeight: 750 }}>{title}</div>
          <div className="li-sub">{sub}</div>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Where the sign-in card is in its flow. Password first; the rest appear only
 *  when they are needed, so the common case stays one form. */
type SignInStep =
  | { at: 'password' }
  | { at: 'code'; minutes: number }
  | { at: 'forgot' }
  | { at: 'reset' }
  | { at: 'done' };

function SignIn() {
  const { signIn, completeOtp } = useSession();
  const [step, setStep] = useState<SignInStep>({ at: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(say(err));
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const minutes = await signIn(email, password);
      /* null means the password was enough and the session already exists —
         this component is about to be replaced, so there is nothing to do. */
      if (minutes !== null) {
        setPassword('');
        setStep({ at: 'code', minutes });
      }
    });
  };

  const submitCode = (e: React.FormEvent) => {
    e.preventDefault();
    void run(() => completeOtp(email, code.trim()));
  };

  const submitForgot = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await auth.forgot(email);
      setCode('');
      setStep({ at: 'reset' });
    });
  };

  const submitReset = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await auth.reset(email, code.trim(), next);
      setPassword('');
      setNext('');
      setCode('');
      setNote('Your password has been changed. Sign in with it.');
      setStep({ at: 'password' });
    });
  };

  const back = (at: SignInStep['at']) => () => {
    setError(null);
    setNote(null);
    setStep({ at } as SignInStep);
  };

  const problem = error && (
    <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginBottom: 10 }}>
      {error}
    </div>
  );

  if (step.at === 'code') {
    return (
      <Card title="Check your email" sub={`We sent a code to ${email}.`}>
        <form onSubmit={submitCode}>
          <label className="li-sub" htmlFor="code">Six-digit code</label>
          <input id="code" className="field mono" value={code} inputMode="numeric"
                 autoComplete="one-time-code" maxLength={6} autoFocus required
                 style={{ marginBottom: 12, letterSpacing: 4, fontSize: 18 }}
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          {problem}
          <button className="btn" style={{ width: '100%' }} type="submit" disabled={busy || code.length < 6}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
        </form>
        <div className="li-sub" style={{ marginTop: 14 }}>
          The code is good for {step.minutes} minutes and can be used once.{' '}
          <button className="btn ghost sm" style={{ marginTop: 8 }} onClick={back('password')}>
            Start again
          </button>
        </div>
      </Card>
    );
  }

  if (step.at === 'forgot') {
    return (
      <Card title="Reset your password" sub="We will email you a code.">
        <form onSubmit={submitForgot}>
          <label className="li-sub" htmlFor="femail">Email</label>
          <input id="femail" className="field" type="email" value={email} autoComplete="username"
                 required autoFocus style={{ marginBottom: 12 }}
                 onChange={(e) => setEmail(e.target.value)} />
          {problem}
          <button className="btn" style={{ width: '100%' }} type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </form>
        <div className="li-sub" style={{ marginTop: 14 }}>
          <button className="btn ghost sm" onClick={back('password')}>Back to sign in</button>
        </div>
      </Card>
    );
  }

  if (step.at === 'reset') {
    return (
      <Card title="Choose a new password" sub={`Use the code we sent to ${email}.`}>
        <form onSubmit={submitReset}>
          <label className="li-sub" htmlFor="rcode">Six-digit code</label>
          <input id="rcode" className="field mono" value={code} inputMode="numeric"
                 autoComplete="one-time-code" maxLength={6} autoFocus required
                 style={{ marginBottom: 10, letterSpacing: 4, fontSize: 18 }}
                 onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          <label className="li-sub" htmlFor="rpw">New password</label>
          <input id="rpw" className="field" type="password" value={next} autoComplete="new-password"
                 required minLength={12} style={{ marginBottom: 12 }}
                 onChange={(e) => setNext(e.target.value)} />
          {problem}
          <button className="btn" style={{ width: '100%' }} type="submit"
                  disabled={busy || code.length < 6 || next.length < 12}>
            {busy ? 'Saving…' : 'Set password'}
          </button>
        </form>
        <div className="li-sub" style={{ marginTop: 14 }}>
          At least 12 characters. Every signed-in session ends when the password
          changes, here and everywhere else.{' '}
          <button className="btn ghost sm" style={{ marginTop: 8 }} onClick={back('forgot')}>
            Ask for another code
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Spark EV Admin" sub="Sign in to the operations console.">
      <form onSubmit={submitPassword}>
        <label className="li-sub" htmlFor="email">Email</label>
        <input id="email" className="field" type="email" value={email} autoComplete="username"
               required onChange={(e) => setEmail(e.target.value)} style={{ marginBottom: 10 }} />
        <label className="li-sub" htmlFor="pw">Password</label>
        <input id="pw" className="field" type="password" value={password} autoComplete="current-password"
               required onChange={(e) => setPassword(e.target.value)} style={{ marginBottom: 12 }} />
        {note && (
          <div className="li-sub" style={{ color: 'var(--good)', marginBottom: 10 }}>{note}</div>
        )}
        {problem}
        <button className="btn" style={{ width: '100%' }} type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <div style={{ marginTop: 10 }}>
        <button className="btn ghost sm" onClick={back('forgot')}>Forgot password?</button>
      </div>
      <div className="li-sub" style={{ marginTop: 14 }}>
        Accounts are created by a super admin from Users &amp; roles. There is no
        self sign-up: this console can block chargers and move money.
      </div>
    </Card>
  );
}

/** A bootstrap account can reach nothing else until its generated password is
 *  replaced — the server enforces this, and the console follows rather than
 *  deciding it. */
function ChangePassword() {
  const { changePassword, signOut } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = again.length > 0 && next !== again;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
    } catch (err) {
      setError(say(err));
      setBusy(false);
    }
  };

  return (
    <Card title="Choose a password" sub="This account is still on the password it was created with.">
      <form onSubmit={submit}>
        <label className="li-sub" htmlFor="cur">Current password</label>
        <input id="cur" className="field" type="password" value={current} autoComplete="current-password"
               required onChange={(e) => setCurrent(e.target.value)} style={{ marginBottom: 10 }} />
        <label className="li-sub" htmlFor="new">New password</label>
        <input id="new" className="field" type="password" value={next} autoComplete="new-password"
               required minLength={12} onChange={(e) => setNext(e.target.value)} style={{ marginBottom: 10 }} />
        <label className="li-sub" htmlFor="again">New password again</label>
        <input id="again" className="field" type="password" value={again} autoComplete="new-password"
               required onChange={(e) => setAgain(e.target.value)} style={{ marginBottom: 12 }} />
        {mismatch && (
          <div className="li-sub" style={{ color: 'var(--bad)', marginBottom: 10 }}>
            The two new passwords do not match.
          </div>
        )}
        {error && (
          <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginBottom: 10 }}>
            {error}
          </div>
        )}
        <button className="btn" style={{ width: '100%' }} type="submit" disabled={busy || mismatch}>
          {busy ? 'Saving…' : 'Set password'}
        </button>
      </form>
      <div className="li-sub" style={{ marginTop: 12 }}>
        At least 12 characters. Length matters more than punctuation, so a
        passphrase you can remember beats a short one you cannot.
      </div>
      <button className="btn ghost sm" style={{ width: '100%', marginTop: 10 }} onClick={signOut}>
        Sign out
      </button>
    </Card>
  );
}

/** Guards a route by what the *server* says this account may reach. The server
 *  checks again on every request — this only saves a round trip and an
 *  empty-looking screen. */
function Guard({ screen, children }: { screen: string; children: React.ReactNode }) {
  const { screens } = useSession();
  if (!screens.includes(screen)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  const { user, loading, mustChangePassword, signOut } = useSession();

  // A 401 from anywhere means the session is gone; drop to the sign-in screen
  // rather than leaving the shell up with empty tables behind it.
  useEffect(() => setUnauthorizedHandler(signOut), [signOut]);

  if (loading) {
    return (
      <div className="login">
        <div className="li-sub">Checking your session…</div>
      </div>
    );
  }
  if (!user) return <SignIn />;
  if (mustChangePassword) return <ChangePassword />;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Dashboard />} />
          <Route path="stations" element={<Guard screen="stations"><Stations /></Guard>} />
          <Route path="chargepoints" element={<Guard screen="chargepoints"><ChargePoints /></Guard>} />
          <Route path="connectors" element={<Guard screen="connectors"><Connectors /></Guard>} />
          <Route path="testbench" element={<Guard screen="testbench"><TestBench /></Guard>} />
          {/* The old path meant connectors, which is what it now goes to. */}
          <Route path="chargers" element={<Navigate to="/connectors" replace />} />
          <Route path="ocpp" element={<Guard screen="ocpp"><Ocpp /></Guard>} />
          <Route path="agents" element={<Guard screen="agents"><Agents /></Guard>} />
          <Route path="topups" element={<Guard screen="topups"><TopUps /></Guard>} />
          <Route path="agent-float" element={<Guard screen="topups"><AgentFloat /></Guard>} />
          <Route path="documents" element={<Guard screen="drivers"><Documents /></Guard>} />
          <Route path="vehicle-documents" element={<Guard screen="drivers"><VehicleDocuments /></Guard>} />
          <Route path="connector-types" element={<Guard screen="chargepoints"><ConnectorTypes /></Guard>} />
          <Route path="charger-models" element={<Guard screen="chargepoints"><ChargerModels /></Guard>} />
          <Route path="releases" element={<Guard screen="releases"><Releases /></Guard>} />
          <Route path="activity" element={<Guard screen="activity"><Activity /></Guard>} />
          <Route path="api-usage" element={<Guard screen="reports"><ApiUsage /></Guard>} />
          <Route path="legal" element={<Guard screen="maps"><Legal /></Guard>} />

          <Route path="drivers" element={<Guard screen="drivers"><Drivers /></Guard>} />
          <Route path="transactions" element={<Guard screen="transactions"><Transactions /></Guard>} />
          <Route path="tariffs" element={<Guard screen="tariffs"><Tariffs /></Guard>} />
          <Route path="disputes" element={<Guard screen="disputes"><Disputes /></Guard>} />
          <Route path="reports" element={<Guard screen="reports"><Usage /></Guard>} />
          <Route path="users" element={<Guard screen="users"><Users /></Guard>} />
          <Route path="maps" element={<Guard screen="maps"><Maps /></Guard>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
